import React, { useEffect, useState } from "react";
import { Plus, Save, Trash2, X, Upload, RefreshCcw, Landmark } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { resolveImageUrl } from "../../lib/imageUrl";

const empty = () => ({
    title: "Yeni Hac Paketi",
    duration: "",
    price: 0,
    price_note: "",
    quota: "",
    mecca_hotel: "",
    medina_hotel: "",
    description: "",
    images: [],
    features: [],
    active: true,
});

const AdminHajj = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState({});
    const loadSeqRef = React.useRef(0);

    const load = async () => {
        const seq = ++loadSeqRef.current;
        setLoading(true);
        try {
            const { data } = await api.get("/admin/hajj-packages");
            if (seq === loadSeqRef.current) setItems(data);
        } catch { toast.error("Paketler yüklenemedi"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const update = (i, field, value) => {
        setItems((cur) => {
            const n = [...cur];
            n[i] = { ...n[i], [field]: field === "price" ? Number(value) || 0 : value };
            return n;
        });
    };

    const addNew = async () => {
        try {
            const { data } = await api.post("/admin/hajj-packages", empty());
            loadSeqRef.current++;
            setItems((cur) => [data, ...cur]);
            toast.success("Yeni paket eklendi — bilgileri doldurup kaydedin");
        } catch { toast.error("Eklenemedi"); }
    };

    const save = async (i) => {
        const p = items[i];
        setSaving((s) => ({ ...s, [p.id]: true }));
        try {
            await api.put(`/admin/hajj-packages/${p.id}`, p);
            toast.success("Kaydedildi");
        } catch { toast.error("Kaydedilemedi"); }
        finally { setSaving((s) => ({ ...s, [p.id]: false })); }
    };

    const remove = async (i) => {
        const p = items[i];
        if (!window.confirm(`"${p.title}" silinsin mi?`)) return;
        try {
            await api.delete(`/admin/hajj-packages/${p.id}`);
            loadSeqRef.current++;
            setItems((cur) => cur.filter((x) => x.id !== p.id));
            toast.success("Silindi");
        } catch { toast.error("Silinemedi"); }
    };

    const uploadImages = async (i, files) => {
        if (!files?.length) return;
        for (const file of files) {
            const fd = new FormData();
            fd.append("file", file);
            try {
                const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                setItems((cur) => {
                    const n = [...cur];
                    n[i] = { ...n[i], images: [...(n[i].images || []), data.url] };
                    return n;
                });
            } catch { toast.error("Görsel yüklenemedi"); }
        }
        toast.success("Görseller yüklendi — kaydet'i unutmayın");
    };

    const removeImage = (i, gi) => {
        setItems((cur) => {
            const n = [...cur];
            n[i] = { ...n[i], images: (n[i].images || []).filter((_, k) => k !== gi) };
            return n;
        });
    };

    const addFeature = (i, value) => {
        const v = String(value || "").trim();
        if (!v) return;
        setItems((cur) => {
            const n = [...cur];
            n[i] = { ...n[i], features: [...(n[i].features || []), v] };
            return n;
        });
    };
    const removeFeature = (i, fi) => {
        setItems((cur) => {
            const n = [...cur];
            n[i] = { ...n[i], features: (n[i].features || []).filter((_, k) => k !== fi) };
            return n;
        });
    };

    return (
        <div>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <h1 className="font-serif text-4xl mb-2">Hac Paketleri</h1>
                    <p className="text-muted_green text-sm">Hac organizasyonu paketleri — fotoğraf, fiyat ve detay bilgileri /hac sayfasında görünür.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load} className="btn-outline-gold !text-xs" data-testid="refresh-hajj"><RefreshCcw className="w-3.5 h-3.5" /> Yenile</button>
                    <button onClick={addNew} className="btn-gold !text-xs" data-testid="add-hajj-package"><Plus className="w-3.5 h-3.5" /> Yeni Paket</button>
                </div>
            </div>

            {loading ? (
                <div className="text-muted_green text-sm">Yükleniyor…</div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-border_green p-12 text-center">
                    <Landmark className="w-12 h-12 text-muted_green mx-auto mb-3" />
                    <div className="text-muted_green text-sm">Henüz hac paketi eklenmemiş. &ldquo;Yeni Paket&rdquo; ile başlayın.</div>
                </div>
            ) : (
                <div className="space-y-6">
                    {items.map((p, i) => (
                        <div key={p.id} className="border border-border_green bg-emerald_surface p-4 sm:p-5" data-testid={`admin-hajj-${p.id}`}>
                            <div className="flex items-center flex-wrap gap-2 mb-4">
                                <input className="input-dark text-lg font-serif flex-1 min-w-[180px]" value={p.title} onChange={(e) => update(i, "title", e.target.value)} placeholder="Paket adı" data-testid={`hajj-title-${p.id}`} />
                                <label className="flex items-center gap-2 text-xs">
                                    <input type="checkbox" checked={!!p.active} onChange={(e) => update(i, "active", e.target.checked)} data-testid={`hajj-active-${p.id}`} />
                                    <span className="text-muted_green">Aktif</span>
                                </label>
                                <button onClick={() => save(i)} disabled={saving[p.id]} className="btn-gold !text-xs" data-testid={`hajj-save-${p.id}`}><Save className="w-3.5 h-3.5" /> Kaydet</button>
                                <button onClick={() => remove(i)} className="text-rose-400 hover:text-rose-500 p-2" data-testid={`hajj-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                            </div>

                            <div className="grid md:grid-cols-2 gap-5">
                                {/* Left: gallery */}
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-gold mb-1">Fotoğraflar ({(p.images || []).length})</div>
                                    <div className="aspect-[16/10] bg-emerald_deep border border-border_green mb-2 overflow-hidden flex items-center justify-center">
                                        {(p.images || [])[0] ? (
                                            <img src={resolveImageUrl(p.images[0])} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <Landmark className="w-12 h-12 text-muted_green" />
                                        )}
                                    </div>
                                    {(p.images || []).length > 1 && (
                                        <div className="grid grid-cols-4 gap-1.5 mb-2">
                                            {(p.images || []).slice(1).map((g, gi) => (
                                                <div key={gi + 1} className="relative aspect-square bg-emerald_deep border border-border_green overflow-hidden">
                                                    <img src={resolveImageUrl(g)} alt="" className="w-full h-full object-cover" />
                                                    <button onClick={() => removeImage(i, gi + 1)} className="absolute top-0.5 right-0.5 bg-rose-600 text-white rounded-full p-0.5">
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {(p.images || [])[0] && (
                                        <button onClick={() => removeImage(i, 0)} className="text-[10px] text-rose-400 hover:text-rose-500 mb-2">Kapak görselini kaldır</button>
                                    )}
                                    <label className="btn-outline-gold !text-xs w-full justify-center cursor-pointer">
                                        <Upload className="w-3.5 h-3.5" /> Fotoğraf Yükle (çoklu seçilebilir)
                                        <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { uploadImages(i, Array.from(e.target.files || [])); e.target.value = ""; }} data-testid={`hajj-upload-${p.id}`} />
                                    </label>
                                </div>

                                {/* Right: fields */}
                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="label-dark">Süre</label>
                                            <input className="input-dark" value={p.duration} onChange={(e) => update(i, "duration", e.target.value)} placeholder="Örn: 21 gün" data-testid={`hajj-duration-${p.id}`} />
                                        </div>
                                        <div>
                                            <label className="label-dark">Kontenjan</label>
                                            <input className="input-dark" value={p.quota} onChange={(e) => update(i, "quota", e.target.value)} placeholder="Örn: 40 kişi" data-testid={`hajj-quota-${p.id}`} />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="label-dark">Fiyat (SAR)</label>
                                            <input type="number" className="input-dark" value={p.price} onChange={(e) => update(i, "price", e.target.value)} data-testid={`hajj-price-${p.id}`} />
                                        </div>
                                        <div>
                                            <label className="label-dark">Fiyat Notu</label>
                                            <input className="input-dark" value={p.price_note} onChange={(e) => update(i, "price_note", e.target.value)} placeholder="Örn: kişi başı / çift kişilik odada" data-testid={`hajj-pricenote-${p.id}`} />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="label-dark">Mekke Oteli</label>
                                            <input className="input-dark" value={p.mecca_hotel} onChange={(e) => update(i, "mecca_hotel", e.target.value)} placeholder="Otel adı" data-testid={`hajj-mecca-${p.id}`} />
                                        </div>
                                        <div>
                                            <label className="label-dark">Medine Oteli</label>
                                            <input className="input-dark" value={p.medina_hotel} onChange={(e) => update(i, "medina_hotel", e.target.value)} placeholder="Otel adı" data-testid={`hajj-medina-${p.id}`} />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="label-dark">Açıklama / Program Bilgisi</label>
                                        <textarea className="input-dark" rows={5} value={p.description} onChange={(e) => update(i, "description", e.target.value)} placeholder="Program detayı, dahil olan hizmetler, ziyaret yerleri..." data-testid={`hajj-desc-${p.id}`} />
                                    </div>
                                    <div>
                                        <label className="label-dark">Dahil Olan Hizmetler</label>
                                        <div className="flex flex-wrap gap-1.5 mb-2">
                                            {(p.features || []).map((f, fi) => (
                                                <span key={fi} className="bg-emerald_deep/60 border border-border_green text-bone text-xs px-2 py-1 inline-flex items-center gap-1">
                                                    {f}
                                                    <button onClick={() => removeFeature(i, fi)} className="text-rose-400 hover:text-rose-500"><X className="w-3 h-3" /></button>
                                                </span>
                                            ))}
                                        </div>
                                        <input
                                            className="input-dark"
                                            placeholder="Hizmet ekle ve Enter (örn: Uçak bileti, Vize, Kurban, Rehberlik)"
                                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeature(i, e.target.value); e.target.value = ""; } }}
                                            data-testid={`hajj-add-feature-${p.id}`}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminHajj;
